import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { api, useAuth } from "@/lib/auth";
import { AccountLayout } from "@/components/account/AccountLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
    ArrowLeft, ArrowRight, Check, Loader2, Wallet, CreditCard,
    Building2, Target, Calendar, Save, ShoppingCart, Info,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SS_KEY = "noxn.wizard.v2";

const STEPS = [
    { key: "details", label: "Campaign details", icon: Building2 },
    { key: "targeting", label: "Targeting", icon: Target },
    { key: "plays", label: "Number of plays", icon: ShoppingCart },
    { key: "summary", label: "Pricing summary", icon: Wallet },
    { key: "payment", label: "Payment", icon: CreditCard },
];

const emptyValues = () => ({
    campaign_goal: "awareness",
    goal_other_text: "",
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    launch_date: "",
    cities: [],
    ad_space_details: {
        screen_id: null, screen_name: "", screen_city: "",
        screen_play_weight: 1,
        audio: false, notes: "",
    },
    services: ["ad_space"],
    notes: "",
    plays: 200,
});

const cents = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);
const dollars = (c) => (c == null ? "$0.00" : `$${(c / 100).toFixed(2)}`);

export default function AccountBookWizard() {
    usePageMeta({ title: "Book a campaign · NOXN", description: "Plan and pay for your NOXN campaign." });
    const { user } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();

    // Persist wizard state across reloads
    const [step, setStep] = useState(0);
    const [values, setValues] = useState(() => {
        try {
            const raw = sessionStorage.getItem(SS_KEY);
            if (raw) return { ...emptyValues(), ...JSON.parse(raw) };
        } catch (e) { /* ignore */ }
        const v = emptyValues();
        if (user?.email) v.email = user.email;
        return v;
    });

    const [tiers, setTiers] = useState([]);
    const [minPurchase, setMinPurchase] = useState(200);
    const [quote, setQuote] = useState(null);
    const [balance, setBalance] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [assessmentId, setAssessmentId] = useState(() => {
        try { return sessionStorage.getItem("noxn.wizard.v2.assessment_id"); } catch (e) { return null; }
    });

    // Persist assessmentId so it survives a Stripe redirect round-trip.
    useEffect(() => {
        try {
            if (assessmentId) sessionStorage.setItem("noxn.wizard.v2.assessment_id", assessmentId);
            else sessionStorage.removeItem("noxn.wizard.v2.assessment_id");
        } catch (e) { /* ignore */ }
    }, [assessmentId]);

    // Save state on every change
    useEffect(() => {
        try { sessionStorage.setItem(SS_KEY, JSON.stringify(values)); } catch (e) { /* ignore */ }
    }, [values]);

    // Load tiers + balance
    useEffect(() => {
        axios.get(`${API}/plays/tiers/active`).then(({ data }) => {
            setTiers(data.tiers || []);
            setMinPurchase(data.min_purchase || 200);
            setValues((v) => v.plays < (data.min_purchase || 200)
                ? { ...v, plays: data.min_purchase || 200 } : v);
        }).catch(() => { });
    }, []);

    useEffect(() => {
        if (!user) return;
        api.get("/plays/balance").then(({ data }) => setBalance(data.balance || 0))
            .catch(() => setBalance(0));
    }, [user]);

    // Auto-resume flow after a plays top-up (Wave 3 payment-return wiring):
    // if the URL flags us as returning from checkout, jump to Payment and
    // finalise against the freshly-credited balance.
    const [autoConfirmAttempted, setAutoConfirmAttempted] = useState(false);
    useEffect(() => {
        if (params.get("resumed_from_topup") !== "1") return;
        // Wait until balance has loaded before deciding
        if (balance == null) return;
        setStep(4);
    }, [params, balance]);

    // Prefill screen selection from ?screen=<id>
    useEffect(() => {
        const sid = params.get("screen");
        if (!sid || values.ad_space_details.screen_id === sid) return;
        axios.get(`${API}/screens/public/map`).then(({ data }) => {
            const s = (data || []).find((x) => x.id === sid);
            if (s) {
                setValues((v) => ({
                    ...v,
                    cities: v.cities.includes(s.city) ? v.cities : [...v.cities, s.city],
                    ad_space_details: {
                        ...v.ad_space_details,
                        screen_id: s.id, screen_name: s.name, screen_city: s.city,
                        screen_play_weight: Number(s.play_weight) || 1,
                    },
                }));
            }
        }).catch(() => { });
    }, [params]);

    // Live quote whenever plays changes
    useEffect(() => {
        if (!values.plays || values.plays < minPurchase) { setQuote(null); return; }
        const ctrl = new AbortController();
        axios.post(`${API}/plays/quote`, { plays: Number(values.plays), user_id: user?.id },
            { signal: ctrl.signal })
            .then(({ data }) => setQuote(data))
            .catch(() => setQuote(null));
        return () => ctrl.abort();
    }, [values.plays, minPurchase, user?.id]);

    const currentStep = STEPS[step];

    const validate = useCallback(() => {
        const errs = {};
        if (step === 0) {
            if (!values.business_name.trim()) errs.business_name = "Required";
            if (!values.contact_name.trim()) errs.contact_name = "Required";
            if (!values.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errs.email = "Enter a valid email";
            if (!values.launch_date) errs.launch_date = "Pick a launch date";
        }
        if (step === 1) {
            if (values.cities.length === 0) errs.cities = "Choose at least one city";
        }
        if (step === 2) {
            const n = Number(values.plays);
            if (!n || n < minPurchase) errs.plays = `Minimum ${minPurchase.toLocaleString()} plays`;
        }
        return errs;
    }, [step, values, minPurchase]);

    const errors = validate();
    const canContinue = Object.keys(errors).length === 0;

    const next = () => canContinue && setStep((s) => Math.min(STEPS.length - 1, s + 1));
    const prev = () => setStep((s) => Math.max(0, s - 1));

    const saveAndExit = () => {
        toast.success("Progress saved. Resume anytime from your dashboard.");
        navigate("/dashboard");
    };

    // Create the assessment when we hit the payment step (once)
    const createCampaign = async () => {
        setSubmitting(true);
        try {
            const payload = {
                form_category: "cat2",
                campaign_goal: values.campaign_goal,
                goal_other_text: values.goal_other_text || null,
                services: values.services,
                ad_space: {
                    screen_id: values.ad_space_details.screen_id,
                    screen_name: values.ad_space_details.screen_name,
                    screen_city: values.ad_space_details.screen_city,
                    audio: !!values.ad_space_details.audio,
                    notes: values.ad_space_details.notes || null,
                    cities: values.cities,
                },
                plays: Number(values.plays),
                launch_date: values.launch_date,
                business_name: values.business_name.trim(),
                contact_name: values.contact_name.trim(),
                email: values.email.trim(),
                phone: values.phone.trim() || null,
                notes: values.notes || null,
            };
            const { data } = await axios.post(`${API}/assessments`, payload, {
                headers: user ? { Authorization: `Bearer ${localStorage.getItem("noxn.token") || ""}` } : {},
                withCredentials: true,
            });
            setAssessmentId(data.id);
            return data;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not create your campaign booking.");
            throw e;
        } finally { setSubmitting(false); }
    };

    // Called from summary → payment step transition
    const goToPayment = async () => {
        if (assessmentId) { setStep(4); return; }
        try {
            await createCampaign();
            setStep(4);
        } catch (e) { /* toast already fired */ }
    };

    const confirmWithBalance = useCallback(async () => {
        if (!assessmentId) return;
        setSubmitting(true);
        try {
            await api.post("/campaigns/confirm-with-balance", { assessment_id: assessmentId });
            toast.success("Campaign confirmed with your play balance.");
            sessionStorage.removeItem(SS_KEY);
            sessionStorage.removeItem("noxn.wizard.v2.assessment_id");
            navigate(`/dashboard?campaign=${assessmentId}`);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (typeof detail === "object" && detail?.detail === "Insufficient play balance") {
                toast.error(`You need ${detail.shortfall.toLocaleString()} more plays.`);
            } else {
                toast.error(typeof detail === "string" ? detail : "Could not confirm.");
            }
        } finally { setSubmitting(false); }
    }, [assessmentId, navigate]);

    // After a top-up returns us here, if the balance is now sufficient,
    // finalise the booking automatically once. `autoConfirmAttempted` guard
    // stops us re-running on state changes.
    useEffect(() => {
        if (params.get("resumed_from_topup") !== "1") return;
        if (autoConfirmAttempted) return;
        if (balance == null || !assessmentId) return;
        const needed = Number(values.plays || 0);
        if (needed > 0 && balance >= needed) {
            setAutoConfirmAttempted(true);
            toast.success("Balance top-up received — finalising your booking…");
            confirmWithBalance();
        }
    }, [params, balance, assessmentId, values.plays, autoConfirmAttempted, confirmWithBalance]);

    const buyPlaysCheckout = async (playsToBuy) => {
        setSubmitting(true);
        try {
            const { data } = await api.post("/plays/checkout", {
                plays: Number(playsToBuy),
                origin_url: window.location.origin,
            });
            if (data.checkout_url) window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not start checkout.");
        } finally { setSubmitting(false); }
    };

    const totalCents = quote?.total_cents ?? 0;
    const gstCents = Math.round(totalCents * 0.10);
    const grandCents = totalCents + gstCents;
    const balanceSafe = balance ?? 0;
    const shortfall = Math.max(0, Number(values.plays || 0) - balanceSafe);

    return (
        <AccountLayout title="Book a campaign">
            <div className="pb-24" data-testid="account-book-wizard">
                <ProgressBar step={step} onJump={(i) => i < step && setStep(i)} />

                <div className="mt-8">
                    {step === 0 && <StepDetails values={values} setValues={setValues} errors={errors} />}
                    {step === 1 && <StepTargeting values={values} setValues={setValues} errors={errors} />}
                    {step === 2 && (
                        <StepPlays
                            values={values} setValues={setValues} errors={errors}
                            tiers={tiers} minPurchase={minPurchase} quote={quote} balance={balanceSafe}
                        />
                    )}
                    {step === 3 && (
                        <StepSummary
                            values={values} quote={quote} balance={balanceSafe}
                            totalCents={totalCents} gstCents={gstCents} grandCents={grandCents}
                        />
                    )}
                    {step === 4 && (
                        <StepPayment
                            values={values} balance={balanceSafe} shortfall={shortfall}
                            grandCents={grandCents} submitting={submitting}
                            confirmWithBalance={confirmWithBalance}
                            buyPlaysCheckout={buyPlaysCheckout}
                            assessmentId={assessmentId}
                        />
                    )}
                </div>

                <Nav
                    step={step} lastStep={STEPS.length - 1} canContinue={canContinue}
                    onBack={prev}
                    onContinue={step === 3 ? goToPayment : next}
                    onSaveExit={saveAndExit}
                    submitting={submitting}
                />
            </div>
        </AccountLayout>
    );
}

const ProgressBar = ({ step, onJump }) => (
    <div className="flex items-center gap-2 flex-wrap" data-testid="wizard-progress">
        {STEPS.map((s, i) => {
            const done = i < step, active = i === step;
            const Icon = s.icon;
            return (
                <button
                    key={s.key}
                    onClick={() => onJump(i)}
                    data-testid={`wizard-step-btn-${i}`}
                    disabled={i >= step}
                    className={`flex items-center gap-2 px-3 py-2 text-[10px] font-mono-tech uppercase tracking-[0.25em] border ${
                        active ? "border-[#FF5A00] text-white bg-[#FF5A00]/10" :
                        done ? "border-emerald-500/40 text-emerald-300 hover:bg-white/5" :
                        "border-white/10 text-slate-500"
                    } transition-colors`}
                >
                    <span className="grid place-items-center w-5 h-5 border border-current text-[10px]">
                        {done ? <Check size={11} /> : i + 1}
                    </span>
                    <span className="hidden sm:inline">{s.label}</span>
                    <Icon size={12} className="sm:hidden" />
                </button>
            );
        })}
    </div>
);

const H = ({ n, title, sub }) => (
    <div className="mb-8">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00] mb-3">
            Step {n} of 5
        </div>
        <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-white">{title}</h2>
        {sub && <p className="mt-3 text-sm text-slate-400 max-w-2xl">{sub}</p>}
    </div>
);

const Field = ({ label, hint, error, required, children }) => (
    <label className="block">
        <div className="flex items-baseline justify-between mb-1.5">
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-400">
                {label}{required && <span className="text-[#FF5A00] ml-0.5">*</span>}
            </span>
            {hint && <span className="text-[10px] text-slate-500">{hint}</span>}
        </div>
        {children}
        {error && <div className="mt-1.5 text-xs text-red-400">{error}</div>}
    </label>
);

const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#FF5A00] px-3 py-2.5 text-sm outline-none transition-colors";

// Step 1 — Campaign details
const StepDetails = ({ values, setValues, errors }) => (
    <div>
        <H n={1} title="Tell us about the campaign" sub="Just the essentials — you can add creative & screens next." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            <Field label="Business name" required error={errors.business_name}>
                <input value={values.business_name} onChange={(e) => setValues({ ...values, business_name: e.target.value })}
                    data-testid="wizard-business-name" className={inputCls} placeholder="Acme Coffee Co." />
            </Field>
            <Field label="Contact name" required error={errors.contact_name}>
                <input value={values.contact_name} onChange={(e) => setValues({ ...values, contact_name: e.target.value })}
                    data-testid="wizard-contact-name" className={inputCls} placeholder="Jane Smith" />
            </Field>
            <Field label="Email" required error={errors.email}>
                <input type="email" value={values.email} onChange={(e) => setValues({ ...values, email: e.target.value })}
                    data-testid="wizard-email" className={inputCls} placeholder="jane@company.com" />
            </Field>
            <Field label="Phone" hint="Optional">
                <input value={values.phone} onChange={(e) => setValues({ ...values, phone: e.target.value })}
                    data-testid="wizard-phone" className={inputCls} placeholder="+61 4xx xxx xxx" />
            </Field>
            <Field label="Launch date" required error={errors.launch_date}>
                <input type="date" value={values.launch_date}
                    onChange={(e) => setValues({ ...values, launch_date: e.target.value })}
                    data-testid="wizard-launch-date" className={inputCls} />
            </Field>
            <Field label="Campaign goal" hint="Awareness works for most launches">
                <select value={values.campaign_goal} onChange={(e) => setValues({ ...values, campaign_goal: e.target.value })}
                    data-testid="wizard-goal" className={inputCls}>
                    <option value="awareness">Brand awareness</option>
                    <option value="promo">Sale or promotion</option>
                    <option value="event">Event countdown</option>
                    <option value="recruitment">Recruitment</option>
                    <option value="listing">Property / listing</option>
                    <option value="other">Other</option>
                </select>
            </Field>
        </div>
    </div>
);

// Step 2 — Targeting
const CITIES = ["Brisbane", "Sydney", "Melbourne"];
const StepTargeting = ({ values, setValues, errors }) => {
    const toggleCity = (c) => {
        const cur = values.cities || [];
        setValues({ ...values, cities: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] });
    };
    return (
        <div>
            <H n={2} title="Where should it run?" sub="Pick one or more cities. Individual screens can be locked in from the map after booking." />
            <div className="max-w-3xl">
                <Field label="Target cities" required error={errors.cities}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {CITIES.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => toggleCity(c)}
                                data-testid={`wizard-city-${c.toLowerCase()}`}
                                className={`p-4 border transition-colors text-left ${
                                    values.cities.includes(c)
                                        ? "border-[#FF5A00] bg-[#FF5A00]/10 text-white"
                                        : "border-white/15 hover:border-white/30 text-slate-300"
                                }`}
                            >
                                <div className="font-display font-black text-lg">{c}</div>
                                <div className="text-xs text-slate-500 mt-1">Digital billboards</div>
                            </button>
                        ))}
                    </div>
                </Field>

                {values.ad_space_details.screen_id && (
                    <div className="mt-6 p-4 border border-[#FF5A00]/40 bg-[#FF5A00]/5 text-sm" data-testid="wizard-preselected-screen">
                        Screen selected: <strong>{values.ad_space_details.screen_name}</strong> · {values.ad_space_details.screen_city}
                    </div>
                )}

                <div className="mt-6 grid grid-cols-1 gap-6">
                    <Field label="Requirements or notes" hint="Optional">
                        <textarea rows={4}
                            value={values.notes} onChange={(e) => setValues({ ...values, notes: e.target.value })}
                            data-testid="wizard-notes"
                            className={inputCls + " resize-none"}
                            placeholder="e.g. audio required, specific dwell time, avoid overlap with a competitor" />
                    </Field>
                    <label className="inline-flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!values.ad_space_details.audio}
                            onChange={(e) => setValues({
                                ...values,
                                ad_space_details: { ...values.ad_space_details, audio: e.target.checked },
                            })}
                            data-testid="wizard-audio"
                            className="w-4 h-4 accent-[#FF5A00]"
                        />
                        Audio-enabled placement preferred (where available)
                    </label>
                </div>
            </div>
        </div>
    );
};

// Step 3 — Number of plays
const StepPlays = ({ values, setValues, errors, tiers, minPurchase, quote, balance }) => {
    const setPlays = (n) => setValues({ ...values, plays: Math.max(0, Math.floor(Number(n) || 0)) });
    const quickPicks = [minPurchase, 500, 1000, 2500, 5000];
    return (
        <div>
            <H n={3} title="How many plays?" sub={`You only pay for the plays you buy. Higher volumes unlock a better rate per play. Minimum ${minPurchase.toLocaleString()} plays.`} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2 space-y-6">
                    <Field label="Plays" required error={errors.plays} hint={`Balance ${balance.toLocaleString()}`}>
                        <div className="flex items-stretch gap-2">
                            <button onClick={() => setPlays(values.plays - 100)}
                                data-testid="wizard-plays-dec"
                                className="w-12 border border-white/15 hover:border-[#FF5A00] text-lg">−</button>
                            <input
                                type="number" min={0} step={100}
                                value={values.plays}
                                onChange={(e) => setPlays(e.target.value)}
                                data-testid="wizard-plays-input"
                                className={inputCls + " text-center text-2xl font-display font-black"}
                            />
                            <button onClick={() => setPlays(Number(values.plays) + 100)}
                                data-testid="wizard-plays-inc"
                                className="w-12 border border-white/15 hover:border-[#FF5A00] text-lg">+</button>
                        </div>
                    </Field>
                    <div className="flex items-center gap-2 flex-wrap">
                        {quickPicks.filter((q) => q >= minPurchase).map((q) => (
                            <button
                                key={q} onClick={() => setPlays(q)}
                                data-testid={`wizard-plays-quick-${q}`}
                                className={`px-3 py-1.5 text-xs font-mono-tech uppercase tracking-widest border transition-colors ${
                                    values.plays === q
                                        ? "border-[#FF5A00] text-white bg-[#FF5A00]/10"
                                        : "border-white/15 text-slate-400 hover:border-white/40"
                                }`}
                            >
                                {q.toLocaleString()}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {tiers.map((t) => {
                            const active = quote?.tier_id === t.id;
                            return (
                                <div
                                    key={t.id}
                                    data-testid={`wizard-tier-${t.id}`}
                                    className={`p-4 border ${active ? "border-[#FF5A00] bg-[#FF5A00]/10" : "border-white/10"}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-display font-black text-white">{t.label}</div>
                                        <div className="text-[#FF5A00] font-mono-tech text-sm">{cents(t.price_per_play_cents)}/play</div>
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1 font-mono-tech">
                                        {t.min_plays.toLocaleString()}{t.max_plays ? `–${t.max_plays.toLocaleString()}` : "+"} plays
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="lg:sticky lg:top-6 h-fit border border-[#FF5A00]/30 bg-[#FF5A00]/5 p-6" data-testid="wizard-quote-panel">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-3">/ Live quote</div>
                    {quote ? (
                        <>
                            <div className="text-sm text-slate-300">Tier</div>
                            <div className="font-display font-black text-2xl text-white">{quote.label}</div>
                            <div className="mt-4 text-sm text-slate-300">Price per play</div>
                            <div className="text-lg text-[#FF5A00] font-mono-tech">{cents(quote.price_per_play_cents)}</div>
                            <div className="mt-4 text-sm text-slate-300">Subtotal ({values.plays.toLocaleString()} plays)</div>
                            <div className="font-display font-black text-3xl text-white">{dollars(quote.total_cents)}</div>
                            <div className="mt-1 text-xs text-slate-500">+ GST 10% at checkout</div>
                        </>
                    ) : (
                        <div className="text-sm text-slate-400">Enter a valid quantity to see the price.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Step 4 — Pricing summary
const StepSummary = ({ values, quote, balance, totalCents, gstCents, grandCents }) => (
    <div>
        <H n={4} title="Review before payment" sub="Everything is stored the moment you confirm — pricing you see here is what you pay." />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
                <SummaryBlock title="Campaign">
                    <Row k="Business" v={values.business_name} />
                    <Row k="Contact" v={values.contact_name} />
                    <Row k="Email" v={values.email} />
                    {values.phone && <Row k="Phone" v={values.phone} />}
                    <Row k="Launch date" v={values.launch_date} />
                    <Row k="Goal" v={values.campaign_goal} />
                </SummaryBlock>
                <SummaryBlock title="Targeting">
                    <Row k="Cities" v={values.cities.join(", ") || "—"} />
                    {values.ad_space_details.screen_name && <Row k="Screen" v={`${values.ad_space_details.screen_name} · ${values.ad_space_details.screen_city}`} />}
                    <Row k="Plays per spot" v={`${values.ad_space_details.screen_play_weight || 1} play${(values.ad_space_details.screen_play_weight || 1) === 1 ? "" : "s"}/spot`} />
                    <Row k="Estimated spots" v={Math.floor(Number(values.plays || 0) / Math.max(1, Number(values.ad_space_details.screen_play_weight || 1))).toLocaleString()} />
                    <Row k="Audio" v={values.ad_space_details.audio ? "Yes" : "No"} />
                    {values.notes && <Row k="Notes" v={values.notes} />}
                </SummaryBlock>
            </div>
            <div className="border border-[#FF5A00]/40 bg-[#FF5A00]/5 p-6 h-fit" data-testid="wizard-summary-price">
                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-4">/ Cost</div>
                <Row k="Tier" v={quote?.label || "—"} />
                <Row k="Plays" v={Number(values.plays).toLocaleString()} />
                <Row k="Rate" v={quote ? `${cents(quote.price_per_play_cents)}/play` : "—"} />
                <div className="my-4 border-t border-white/10" />
                <Row k="Subtotal" v={dollars(totalCents)} />
                <Row k="GST (10%)" v={dollars(gstCents)} />
                <div className="my-4 border-t border-white/10" />
                <div className="flex items-center justify-between">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400">Total</div>
                    <div className="font-display font-black text-3xl text-white">{dollars(grandCents)}</div>
                </div>
                <div className="mt-4 text-xs text-slate-400 flex items-start gap-2">
                    <Wallet size={12} className="text-[#FF5A00] mt-0.5 shrink-0" />
                    Your balance: <strong className="text-white">{balance.toLocaleString()} plays</strong>
                </div>
            </div>
        </div>
    </div>
);

const SummaryBlock = ({ title, children }) => (
    <div className="border border-white/10 p-6">
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-4">/ {title}</div>
        <div className="space-y-2">{children}</div>
    </div>
);
const Row = ({ k, v }) => (
    <div className="flex items-baseline justify-between gap-4 text-sm">
        <div className="text-slate-400">{k}</div>
        <div className="text-white text-right">{v}</div>
    </div>
);

// Step 5 — Payment
const StepPayment = ({ values, balance, shortfall, grandCents, submitting, confirmWithBalance, buyPlaysCheckout, assessmentId }) => {
    const enough = shortfall <= 0;
    const needToBuy = shortfall;
    return (
        <div>
            <H n={5} title="Confirm payment" sub={enough
                ? "You have enough plays in your balance. One click and we lock the campaign in."
                : "You need a few more plays. Top up your balance and we'll confirm the campaign on return."} />
            {!assessmentId ? (
                <div className="border border-yellow-500/40 bg-yellow-500/5 p-6 text-sm text-yellow-100" data-testid="wizard-payment-pending">
                    <Loader2 className="inline animate-spin mr-2" size={14} /> Preparing your booking…
                </div>
            ) : enough ? (
                <div className="border border-emerald-500/40 bg-emerald-500/5 p-6" data-testid="wizard-payment-balance">
                    <div className="flex items-start gap-3">
                        <Wallet className="text-emerald-400 shrink-0" size={20} />
                        <div>
                            <div className="font-display font-black text-xl text-white">Use existing balance</div>
                            <p className="mt-1 text-sm text-slate-300">
                                Confirming will debit <strong className="text-white">{Number(values.plays).toLocaleString()} plays</strong> from
                                your balance of {balance.toLocaleString()}. You&apos;ll have {(balance - Number(values.plays)).toLocaleString()} plays remaining.
                            </p>
                            <button
                                onClick={confirmWithBalance}
                                disabled={submitting}
                                data-testid="wizard-confirm-balance"
                                className="mt-4 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-slate-900 px-6 py-3 text-xs font-semibold uppercase tracking-widest transition-colors disabled:opacity-50"
                            >
                                {submitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                                Confirm with balance
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="border border-[#FF5A00]/40 bg-[#FF5A00]/5 p-6" data-testid="wizard-payment-topup">
                    <div className="flex items-start gap-3">
                        <CreditCard className="text-[#FF5A00] shrink-0" size={20} />
                        <div>
                            <div className="font-display font-black text-xl text-white">Buy the plays you need</div>
                            <p className="mt-1 text-sm text-slate-300">
                                Balance: <strong>{balance.toLocaleString()}</strong> · Need: <strong>{Number(values.plays).toLocaleString()}</strong> · Short by <strong className="text-[#FF5A00]">{needToBuy.toLocaleString()} plays</strong>.
                            </p>
                            <p className="mt-2 text-xs text-slate-400 flex items-start gap-2">
                                <Info size={12} className="text-slate-500 mt-0.5" />
                                After a successful purchase we&apos;ll credit the plays to your balance and you can confirm the campaign in one tap.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                <button
                                    onClick={() => buyPlaysCheckout(needToBuy)}
                                    disabled={submitting}
                                    data-testid="wizard-buy-shortfall"
                                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest transition-colors disabled:opacity-50"
                                >
                                    {submitting ? <Loader2 size={13} className="animate-spin" /> : <CreditCard size={13} />}
                                    Buy {needToBuy.toLocaleString()} plays
                                </button>
                                <button
                                    onClick={() => buyPlaysCheckout(Number(values.plays))}
                                    disabled={submitting}
                                    data-testid="wizard-buy-full"
                                    className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 text-white px-6 py-3 text-xs font-semibold uppercase tracking-widest transition-colors disabled:opacity-50"
                                >
                                    Buy full {Number(values.plays).toLocaleString()} instead
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <div className="mt-6 text-xs text-slate-500 max-w-2xl">
                Your campaign is saved (id <code className="text-slate-300">{assessmentId || "…"}</code>). If you leave now, you can resume from your dashboard.
            </div>
        </div>
    );
};

// Bottom nav
const Nav = ({ step, lastStep, canContinue, onBack, onContinue, onSaveExit, submitting }) => (
    <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-[#0F1E3B]/95 backdrop-blur px-6 lg:px-10 py-4 flex items-center justify-between gap-4" data-testid="wizard-nav">
        <button
            onClick={onBack}
            disabled={step === 0 || submitting}
            data-testid="wizard-back"
            className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white disabled:opacity-30"
        >
            <ArrowLeft size={13} /> Back
        </button>
        <div className="flex items-center gap-2">
            <button
                onClick={onSaveExit}
                data-testid="wizard-save-exit"
                className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-slate-300 hover:text-white border border-white/15 hover:border-white/40 px-4 py-2.5 transition-colors"
            >
                <Save size={13} /> Save &amp; exit
            </button>
            {step < 4 && (
                <button
                    onClick={onContinue}
                    disabled={!canContinue || submitting}
                    data-testid="wizard-continue"
                    className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] disabled:opacity-40 text-white px-6 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors"
                >
                    {submitting ? <Loader2 size={13} className="animate-spin" /> : (step === lastStep - 1 ? "Continue to payment" : "Continue")}
                    <ArrowRight size={13} />
                </button>
            )}
        </div>
    </div>
);
